import { EanLookupResponseBody } from 'lib/types'
import { ReferralFormInputs } from 'features/contracts/referral/types.ts'
import { Producer } from 'types/producer.ts'
import {
  RegistrationAccount,
  RegistrationContact,
  CounterType,
  EanCaptureType,
  ELProduct,
  MeterType,
  NGProduct,
  Region,
  SettlementFrequency,
  SimulationType,
  Situation
} from '@boltenergy-be/shared'
import { AddressWithCountryAndTownCode } from 'types/general.ts'

// ENUMS

export enum AddContractSteps {
  ADDRESS = 'address',
  REFERRAL = 'referral',
  PRODUCER = 'producer',
  PRODUCT = 'product',
  INSTALMENT_DATA = 'instalmentData',
  METER_DETAILS = 'meterDetails',
  CUSTOMER_DATA = 'customerData',
  CONFIRMATION = 'confirmation',
  DONE = 'done'
}

export type StartDateRange = {
  min: string
  max: string
  initial: string
}

export enum SimulationSalesOffice {
  EXTRA_ADDRESS = 'Extra Address',
  MOVE = 'Move'
}

// TYPES

export type AddContractData = {
  [AddContractSteps.ADDRESS]: AddressFormInputs
  [AddContractSteps.REFERRAL]?: ReferralFormInputs
  [AddContractSteps.PRODUCER]?: Producer
  [AddContractSteps.PRODUCT]: ProductFormInputs
  [AddContractSteps.INSTALMENT_DATA]: InstalmentData
  [AddContractSteps.METER_DETAILS]: MeterDataFormInputs
  [AddContractSteps.CUSTOMER_DATA]: CustomerDataFormInputs
}

type InstalmentData = {
  simulationType: SimulationType
  instalments: {
    electricity: number
    gas?: number
  }
}

export type CurrentStepData =
  | AddressFormInputs
  | ReferralFormInputs
  | Producer
  | ProductFormInputs
  | InstalmentData
  | MeterDataFormInputs
  | CustomerDataFormInputs

export type AddContractStepProps = {
  isMove?: boolean
  setNextStep: (data: CurrentStepData, nextStep: AddContractSteps) => void
  addContractData: AddContractData
  setCurrentStep: (addContractStep: AddContractSteps) => void
  setCustomerNumber?: (customerNumber: string) => void
  eanLookupData?: EanLookupResponseBody
  handleEanLookup?: (deliveryAddress: AddressWithCountryAndTownCode) => void
  setAddContractData: (addContractData: AddContractData) => void
}

export type DoneProps = {
  producer?: AddContractData['producer']
  onExit?: () => void
}

export type AddressFormInputs = {
  deliveryAddress: AddressWithCountryAndTownCode
  needsGas: boolean
  region: Region
}

export type AddressFormInputsWithOptionalPostalCode = Omit<AddressFormInputs, 'deliveryAddress'> & {
  deliveryAddress: Omit<AddressWithCountryAndTownCode, 'postalCode'> & { postalCode?: number }
}

export type ProductFormInputs = {
  electricity: ELProduct
  gas?: NGProduct
}

export type MeterDataFormInputs = {
  contractStartDate: string | null // ISO date format
  counterType: CounterType
  customContractStartDate?: boolean
  docUpload?: FileList | null
  dynamicTariff: boolean
  eanCaptureType: EanCaptureType
  electricity: {
    total?: number
    day?: number
    night?: number
    exclNight?: number
    ean: string
    eanExclNight?: string
    meterNumber?: string
  }
  gas?: {
    total?: number
    ean: string
    meterNumber?: string
  }
  hasExclNight: boolean
  hasExclNightEan: boolean
  hasSolarPanels: boolean
  meterReadingMonth: number | null
  meterType: MeterType
  opportunityId?: string
  situation: Situation
  walloniaSolarPanelsSince2024?: boolean
  settlementFrequency: SettlementFrequency
}

export type CustomerDataFormInputs = Pick<RegistrationAccount, 'company' | 'birthDate'> &
  Pick<RegistrationContact, 'contactMode' | 'firstName' | 'lastName' | 'email' | 'phoneMobile' | 'language'> & {
    customerNumber?: string
    isCompany: boolean
    emailConfirmation: RegistrationContact['email']
    sameCorrespondenceAddress?: boolean
    correspondenceAddress?: AddressWithCountryAndTownCode
    iban: RegistrationAccount['paymentDetails']['ibanNumber']
    paymentMethod: RegistrationAccount['paymentDetails']['paymentMethod']
  }

export type InstalmentStepError = {
  type: 'total' | 'electricity' | 'gas'
  message: string
}

export type Products = {
  electricity: ELProduct
  gas?: NGProduct
}
